import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Shield,
  UserX,
  Users,
  Search,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Calendar,
  FileText,
  RotateCcw,
  UserPlus,
  Upload,
  Download,
  Trash2,
  MoreVertical
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BannedResidentManagement = () => {
  const { user } = useAuth();
  
  // State
  const [bannedResidents, setBannedResidents] = useState([]);
  const [filteredResidents, setFilteredResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResident, setSelectedResident] = useState(null);
  const [showUnbanDialog, setShowUnbanDialog] = useState(false);
  const [unbanning, setUnbanning] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAddBannedDialog, setShowAddBannedDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [addBannedData, setAddBannedData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    phone: '',
    ban_date: '',
    ban_reason: '',
    ban_notes: '',
    staff_name: '',
    restriction_type: 'banned' // Default to banned
  });
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);

  useEffect(() => {
    fetchBannedResidents();
  }, []);

  useEffect(() => {
    // Filter residents based on search term
    const filtered = bannedResidents.filter(resident => 
      resident.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resident.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resident.ban_reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resident.ban_staff_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredResidents(filtered);
  }, [bannedResidents, searchTerm]);

  const fetchBannedResidents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/residents?status=restricted`); // Fetch both banned and DNA
      setBannedResidents(response.data);
    } catch (error) {
      console.error('Error fetching restricted residents:', error);
      toast.error('Failed to load restricted residents');
    } finally {
      setLoading(false);
    }
  };

  const handleUnbanResident = async () => {
    if (!selectedResident) return;
    
    setUnbanning(true);
    try {
      const response = await axios.post(`${API}/residents/${selectedResident.id}/unban`);
      toast.success(`${selectedResident.first_name} ${selectedResident.last_name} has been unbanned successfully`);
      
      // Remove from banned list
      setBannedResidents(prev => prev.filter(r => r.id !== selectedResident.id));
      
      setShowUnbanDialog(false);
      setSelectedResident(null);
    } catch (error) {
      console.error('Error unbanning resident:', error);
      toast.error(error.response?.data?.detail || 'Failed to unban resident');
    } finally {
      setUnbanning(false);
    }
  };

  const handleDeleteResident = async () => {
    if (!selectedResident) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/residents/${selectedResident.id}`);
      toast.success(`${selectedResident.first_name} ${selectedResident.last_name} has been permanently deleted`);
      
      // Remove from banned list
      setBannedResidents(prev => prev.filter(r => r.id !== selectedResident.id));
      setSelectedResident(null);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting resident:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete resident');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAddBannedResident = async () => {
    try {
      const response = await axios.post(`${API}/residents/add-banned`, addBannedData);
      const restrictionLabel = addBannedData.restriction_type === 'dna' ? 'DNA resident' : 'Banned resident';
      toast.success(`${restrictionLabel} added successfully`);
      
      // Reset form
      setAddBannedData({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        phone: '',
        ban_date: '',
        ban_reason: '',
        ban_notes: '',
        staff_name: '',
        restriction_type: 'banned'
      });
      
      setShowAddBannedDialog(false);
      fetchBannedResidents(); // Refresh the list
    } catch (error) {
      console.error('Error adding banned resident:', error);
      toast.error(error.response?.data?.detail || 'Failed to add banned resident');
    }
  };

  const handleBulkImport = async () => {
    if (!bulkImportFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setBulkImporting(true);
    try {
      const text = await bulkImportFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('CSV file must have a header row and at least one data row');
        return;
      }

      // Parse CSV (assuming header: first_name,last_name,date_of_birth,phone,ban_date,ban_reason,ban_notes,staff_name,restriction_type)
      const headers = lines[0].split(',').map(h => h.trim());
      const expectedHeaders = ['first_name', 'last_name', 'date_of_birth', 'phone', 'ban_date', 'ban_reason', 'ban_notes', 'staff_name', 'restriction_type'];
      
      // Validate headers
      const hasRequiredHeaders = ['first_name', 'last_name', 'ban_date', 'ban_reason', 'staff_name'].every(required => 
        headers.includes(required)
      );
      
      if (!hasRequiredHeaders) {
        toast.error('CSV must have columns: first_name, last_name, ban_date, ban_reason, staff_name (date_of_birth, phone, ban_notes, restriction_type are optional)');
        return;
      }

      const bannedResidents = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const resident = {};
        
        headers.forEach((header, index) => {
          if (expectedHeaders.includes(header)) {
            if (header === 'restriction_type') {
              resident[header] = values[index] && ['banned', 'dna'].includes(values[index].toLowerCase()) ? values[index].toLowerCase() : 'banned';
            } else {
              resident[header] = values[index] || (header === 'date_of_birth' || header === 'phone' || header === 'ban_notes' ? null : '');
            }
          }
        });
        
        // Validate required fields
        if (resident.first_name && resident.last_name && resident.ban_date && resident.ban_reason && resident.staff_name) {
          bannedResidents.push(resident);
        }
      }

      if (bannedResidents.length === 0) {
        toast.error('No valid banned residents found in CSV file');
        return;
      }

      // Send to backend
      const response = await axios.post(`${API}/residents/bulk-import-banned`, bannedResidents);
      
      if (response.data.successful > 0) {
        toast.success(`Successfully imported ${response.data.successful} restricted residents`);
      }
      
      if (response.data.failed > 0) {
        toast.error(`Failed to import ${response.data.failed} residents. Check console for details.`);
        console.log('Import errors:', response.data.errors);
      }

      setShowBulkImportDialog(false);
      setBulkImportFile(null);
      fetchBannedResidents(); // Refresh the list
      
    } catch (error) {
      console.error('Error during bulk import:', error);      
      toast.error(error.response?.data?.detail || 'Failed to import restricted residents');
    } finally {
      setBulkImporting(false);
    }
  };

  const downloadCSVTemplate = () => {
    const csvContent = `first_name,last_name,date_of_birth,phone,ban_date,ban_reason,ban_notes,staff_name,restriction_type
John,Doe,1985-06-15,555-0123,2024-01-15,Violent behavior,Threatened staff member,Manager Smith,banned
Jane,Smith,,555-0456,2024-02-20,Trespassing,Found on property after hours,Security Jones,dna`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'banned_residents_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    toast.success('CSV template downloaded');
  };

  const getDaysSinceBan = (banDate) => {
    if (!banDate) return 0;
    const today = new Date();
    const ban = new Date(banDate);
    const diffTime = Math.abs(today - ban);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Banned Resident Management</h1>
            <p className="text-gray-600">Manage residents with ban status</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="card-shadow">
              <CardContent className="p-6">
                <div className="skeleton h-20 w-full rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <UserX className="h-8 w-8 text-red-600" />
            <span>Restricted Resident Management</span>
          </h1>
          <p className="text-gray-600 mt-1">Manage residents with Banned or DNA (Do Not Admit) status</p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex items-center space-x-2">
          {user?.role === 'admin' && (
            <>
              <Button 
                onClick={() => setShowAddBannedDialog(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Restricted Resident
              </Button>
              <Button 
                onClick={() => setShowBulkImportDialog(true)}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                <Upload className="h-4 w-4 mr-2" />
                Bulk Import
              </Button>
            </>
          )}
          <Badge variant="outline" className="px-3 py-1">
            {user?.role?.replace('_', ' ').toUpperCase()}
          </Badge>
          {user?.role === 'admin' && (
            <Badge className="px-3 py-1 bg-red-100 text-red-800">
              Admin Access
            </Badge>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Restricted</p>
                <p className="text-3xl font-bold text-red-600">{bannedResidents.length}</p>
              </div>
              <UserX className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Displayed</p>
                <p className="text-3xl font-bold text-gray-900">{filteredResidents.length}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Admin Status</p>
                <p className="text-lg font-semibold text-gray-900">
                  {user?.role === 'admin' ? 'Can Unban' : 'View Only'}
                </p>
              </div>
              <Shield className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Controls */}
      <Card className="card-shadow">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, ban reason, or staff..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              onClick={fetchBannedResidents}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Admin Warning */}
      {user?.role !== 'admin' && (
        <Card className="card-shadow border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">View Only Access</p>
                <p className="text-sm text-amber-700">
                  Administrator privileges are required to unban residents. You can view banned residents but cannot remove bans.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Banned Residents List */}
      {filteredResidents.length === 0 ? (
        <Card className="card-shadow">
          <CardContent className="p-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {bannedResidents.length === 0 ? 'No Banned Residents' : 'No Matching Results'}
            </h3>
            <p className="text-gray-600">
              {bannedResidents.length === 0 
                ? 'Great! There are currently no residents with banned status.'
                : 'Try adjusting your search terms to find specific residents.'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredResidents.map((resident) => (
            <Card key={resident.id} className="card-shadow hover:card-shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold text-gray-900">
                      {resident.first_name} {resident.last_name}
                    </CardTitle>
                    <CardDescription className="flex items-center space-x-2 mt-1">
                      <Badge className={`${
                        resident.status === 'dna' || resident.restriction_type === 'dna'
                          ? 'bg-orange-100 text-orange-800 border-orange-200' 
                          : 'bg-red-100 text-red-800 border-red-200'
                      }`}>
                        {resident.status === 'dna' || resident.restriction_type === 'dna' ? 'DNA' : 'BANNED'}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        {getDaysSinceBan(resident.ban_date)} days ago
                      </span>
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-3">
                {/* Ban Information */}
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Ban Date</p>
                      <p className="text-sm text-gray-600">{formatDateTime(resident.ban_date)}</p>
                    </div>
                  </div>
                  
                  {resident.ban_reason && (
                    <div className="flex items-start space-x-2">
                      <FileText className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Ban Reason</p>
                        <p className="text-sm text-gray-600">{resident.ban_reason}</p>
                      </div>
                    </div>
                  )}
                  
                  {resident.ban_staff_name && (
                    <div className="flex items-start space-x-2">
                      <Shield className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Banned By</p>
                        <p className="text-sm text-gray-600">{resident.ban_staff_name}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Additional Info */}
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">DOB:</span>
                    <span className="text-gray-700">{formatDate(resident.date_of_birth)}</span>
                  </div>
                  {resident.phone && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Phone:</span>
                      <span className="text-gray-700">{resident.phone}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                {user?.role === 'admin' && (
                  <div className="pt-3 flex items-center space-x-2">
                    <Button
                      onClick={() => {
                        setSelectedResident(resident);
                        setShowUnbanDialog(true);
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      size="sm"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Unban
                    </Button>
                    
                    <Button
                      onClick={() => {
                        setSelectedResident(resident);
                        setShowDeleteDialog(true);
                      }}
                      variant="destructive"
                      size="sm"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Unban Confirmation Dialog */}
      <Dialog open={showUnbanDialog} onOpenChange={setShowUnbanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <RotateCcw className="h-5 w-5 text-green-600" />
              <span>Confirm Unban Resident</span>
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove the ban from this resident? They will be able to be readmitted to the shelter.
            </DialogDescription>
          </DialogHeader>
          
          {selectedResident && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900">
                  {selectedResident.first_name} {selectedResident.last_name}
                </h4>
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  <p><strong>Banned:</strong> {formatDateTime(selectedResident.ban_date)}</p>
                  {selectedResident.ban_reason && (
                    <p><strong>Reason:</strong> {selectedResident.ban_reason}</p>
                  )}
                  {selectedResident.ban_staff_name && (
                    <p><strong>Banned by:</strong> {selectedResident.ban_staff_name}</p>
                  )}
                </div>
              </div>
              
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Important Notes:</p>
                    <ul className="text-sm text-amber-700 mt-1 space-y-1">
                      <li>• The resident's status will change from "banned" to "past"</li>
                      <li>• They will be eligible for readmission through normal intake</li>
                      <li>• A case note will be automatically created documenting this action</li>
                      <li>• This action cannot be undone</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              onClick={() => setShowUnbanDialog(false)}
              variant="outline"
              disabled={unbanning}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUnbanResident}
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={unbanning}
            >
              {unbanning ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Unbanning...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Confirm Unban
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Banned Resident Dialog */}
      <Dialog open={showAddBannedDialog} onOpenChange={setShowAddBannedDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <UserPlus className="h-5 w-5 text-red-600" />
              <span>Add Restricted Resident</span>
            </DialogTitle>
            <DialogDescription>
              Add a resident directly to the restricted list (Banned or DNA) without prior admission to the shelter.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">First Name*</label>
                <Input
                  value={addBannedData.first_name}
                  onChange={(e) => setAddBannedData({...addBannedData, first_name: e.target.value})}
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Last Name*</label>
                <Input
                  value={addBannedData.last_name}
                  onChange={(e) => setAddBannedData({...addBannedData, last_name: e.target.value})}
                  placeholder="Enter last name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Date of Birth</label>
                <Input
                  type="date"
                  value={addBannedData.date_of_birth}
                  onChange={(e) => setAddBannedData({...addBannedData, date_of_birth: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Phone</label>
                <Input
                  value={addBannedData.phone}
                  onChange={(e) => setAddBannedData({...addBannedData, phone: e.target.value})}
                  placeholder="555-0123"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Restriction Type*</label>
                <Select value={addBannedData.restriction_type} onValueChange={(value) => setAddBannedData({...addBannedData, restriction_type: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select restriction type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="banned">Banned</SelectItem>
                    <SelectItem value="dna">DNA (Do Not Admit)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Date*</label>
                <Input
                  type="date"
                  value={addBannedData.ban_date}
                  onChange={(e) => setAddBannedData({...addBannedData, ban_date: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Staff Name*</label>
              <Input
                value={addBannedData.staff_name}
                onChange={(e) => setAddBannedData({...addBannedData, staff_name: e.target.value})}
                placeholder="Staff who issued restriction"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">
                {addBannedData.restriction_type === 'dna' ? 'DNA Reason*' : 'Ban Reason*'}
              </label>
              <Input
                value={addBannedData.ban_reason}
                onChange={(e) => setAddBannedData({...addBannedData, ban_reason: e.target.value})}
                placeholder={addBannedData.restriction_type === 'dna' ? 'Reason for DNA restriction' : 'Reason for ban'}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Additional Notes</label>
              <textarea
                value={addBannedData.ban_notes}
                onChange={(e) => setAddBannedData({...addBannedData, ban_notes: e.target.value})}
                placeholder={`Additional details about the ${addBannedData.restriction_type === 'dna' ? 'DNA restriction' : 'ban'}...`}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 min-h-20"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowAddBannedDialog(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddBannedResident}
              className="bg-red-600 hover:bg-red-700"
              disabled={!addBannedData.first_name || !addBannedData.last_name || !addBannedData.ban_date || !addBannedData.ban_reason || !addBannedData.staff_name}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add to {addBannedData.restriction_type === 'dna' ? 'DNA' : 'Banned'} List
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Upload className="h-5 w-5 text-red-600" />
              <span>Bulk Import Restricted Residents</span>
            </DialogTitle>
            <DialogDescription>
              Import multiple restricted residents (Banned or DNA) from a CSV file. This is ideal for importing your existing restricted lists from paper records.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-800">CSV Format Requirements</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Your CSV file must have these columns (in any order):
                  </p>
                  <ul className="text-sm text-blue-700 mt-2 space-y-1">
                    <li><strong>Required:</strong> first_name, last_name, ban_date (YYYY-MM-DD), ban_reason, staff_name</li>
                    <li><strong>Optional:</strong> date_of_birth (YYYY-MM-DD), phone, ban_notes, restriction_type (banned/dna, defaults to banned)</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <Button
                onClick={downloadCSVTemplate}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Download Template</span>
              </Button>
              <p className="text-sm text-gray-500">Use this template to format your data</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Select CSV File</label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setBulkImportFile(e.target.files[0])}
                className="mt-1"
              />
            </div>
            
            {bulkImportFile && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>File selected:</strong> {bulkImportFile.name}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowBulkImportDialog(false)}
              variant="outline"
              disabled={bulkImporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkImport}
              className="bg-red-600 hover:bg-red-700"
              disabled={!bulkImportFile || bulkImporting}
            >
              {bulkImporting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Restricted Residents
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              <span>Confirm Delete Resident</span>
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this resident? This action cannot be undone and will remove all records.
            </DialogDescription>
          </DialogHeader>
          
          {selectedResident && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900">
                  {selectedResident.first_name} {selectedResident.last_name}
                </h4>
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  <p><strong>Banned:</strong> {formatDateTime(selectedResident.ban_date)}</p>
                  {selectedResident.ban_reason && (
                    <p><strong>Reason:</strong> {selectedResident.ban_reason}</p>
                  )}
                  {selectedResident.ban_staff_name && (
                    <p><strong>Banned by:</strong> {selectedResident.ban_staff_name}</p>
                  )}
                </div>
              </div>
              
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Warning: Permanent Deletion</p>
                    <ul className="text-sm text-red-700 mt-1 space-y-1">
                      <li>• This will permanently delete all resident records</li>
                      <li>• All associated case notes and history will be lost</li>
                      <li>• This action cannot be undone</li>
                      <li>• Consider unbanning instead if the resident may return</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              onClick={() => setShowDeleteDialog(false)}
              variant="outline"
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteResident}
              variant="destructive"
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Confirm Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BannedResidentManagement;